import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { isFunction, isPlainObject } from "es-toolkit";
import { sntLog } from "src/app/common/logger";
import {
  BrokerEventListener,
  BrokerEventListenerWithResources
} from "src/app/sse/broker-event.types";
import { BrokerService } from "src/app/sse/broker.service";
import { SseEvents } from "src/app/sse/sse-events";
import { CURRENT_TAB_FACTORY } from "src/app/tokens/current-tab.token";

export type SSERegister<T = any> = (
  eventTypes: SseEvents[],
  eventHandler: BrokerEventListener
) => void;

export type SSESetupReturnType = Array<
  [SseEvents[], BrokerEventListener | BrokerEventListenerWithResources]
>;

const logger = sntLog("SseComponent");

@Component({ template: "", standalone: true })
export abstract class SseComponent implements OnInit, OnDestroy {
  @Input()
  disableSse: boolean = false;

  private readonly brokerService = inject(BrokerService);

  readonly #idsOfRegisteredListeners: Array<{ id: string; type: SseEvents }> =
    [];

  readonly CURRENT_TAB = inject(CURRENT_TAB_FACTORY)();

  ngOnInit(): void {
    if (!this.disableSse) {
      const setupConfig: SSESetupReturnType = this.setupSSEHandlers();

      if (setupConfig) {
        setupConfig.forEach(
          ([eventTypes, ...callbackOrCallBackWithResourcesFactory]) => {
            const callBacksWithoutResourcesFactories: BrokerEventListener[] = <
              BrokerEventListener[]
            >callbackOrCallBackWithResourcesFactory.filter((x) =>
              isFunction(x)
            );

            const callBacksWithResourcesFactories: BrokerEventListenerWithResources[] =
              <BrokerEventListenerWithResources[]>(
                callbackOrCallBackWithResourcesFactory.filter((x) =>
                  isPlainObject(x)
                )
              );

            eventTypes.forEach((eventType) => {
              [
                ...callBacksWithoutResourcesFactories.map((listener) => ({
                  listener,
                  resourcesFactory: () => Promise.resolve([] as string[])
                })),
                ...callBacksWithResourcesFactories
              ].forEach(({ listener, resourcesFactory }) => {
                const id: string = this.brokerService.addListener({
                  type: eventType,
                  listener,
                  resourcesFactory,
                  tab: this.CURRENT_TAB
                });

                this.#idsOfRegisteredListeners.push({
                  id,
                  type: eventType
                });
              });
            });
          }
        );
      }
    }
  }

  ngOnDestroy() {
    this.#idsOfRegisteredListeners.forEach(({ id, type }) => {
      this.brokerService.removeListener({ id, type });
    });
  }

  abstract setupSSEHandlers(): SSESetupReturnType;
}
